import React, { useState, useEffect, useCallback } from 'react';
import API from '../config';

export default function MasterDirectory() {
  const [tab, setTab] = useState('employees');

  // ── Employee Documents tab ──
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Hired Candidates tab ──
  const [hired, setHired] = useState([]);
  const [hiredLoading, setHiredLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [actionMsg, setActionMsg] = useState({});

  // ── Onboarded Staff tab ──
  const [onboarded, setOnboarded] = useState([]);
  const [onboardedLoading, setOnboardedLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ── Fetch employee docs ──
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/employees?include=assignments`, { headers });
      const data = await res.json();
      setEmployees(Array.isArray(data) ? data : data.employees || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch hired candidates ──
  const fetchHired = useCallback(async () => {
    setHiredLoading(true);
    try {
      const res = await fetch(`${API}/recruitment/candidates?status=HIRED`, { headers });
      const data = await res.json();
      setHired(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setHiredLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch onboarded staff (COMPLETED onboardings) ──
  const fetchOnboarded = useCallback(async () => {
    setOnboardedLoading(true);
    try {
      const res = await fetch(`${API}/admin/onboarding?status=COMPLETED`, { headers });
      const data = await res.json();
      setOnboarded(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setOnboardedLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'employees') fetchEmployees();
    if (tab === 'hired') fetchHired();
    if (tab === 'onboarded') fetchOnboarded();
  }, [tab, fetchEmployees, fetchHired, fetchOnboarded]);

  // ── Actions ──
  const doAction = async (candidateId, endpoint, label) => {
    setActionLoading(p => ({ ...p, [candidateId]: label }));
    setActionMsg(p => ({ ...p, [candidateId]: '' }));
    try {
      const res = await fetch(`${API}/recruitment/candidates/${candidateId}/${endpoint}`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Action failed');
      setActionMsg(p => ({ ...p, [candidateId]: data.message || 'Done' }));
      fetchHired();
    } catch (e) {
      setActionMsg(p => ({ ...p, [candidateId]: e.message }));
    } finally {
      setActionLoading(p => ({ ...p, [candidateId]: null }));
    }
  };

  const pendingCount = hired.filter(c => !c.allDocumentsSigned).length;

  // ── Helpers ──
  const fmt = dateStr => dateStr ? new Date(dateStr).toLocaleString() : null;
  const step = (done, label) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 12, fontSize: 12,
      background: done ? '#d1fae5' : '#f3f4f6',
      color: done ? '#065f46' : '#6b7280',
      fontWeight: done ? 600 : 400,
    }}>
      {done ? '✓' : '○'} {label}
    </span>
  );

  const cardBorder = c => {
    if (c.allDocumentsSigned) return '#10b981';
    if (c.documentsEmailSentAt) return '#3b82f6';
    if (c.documentsGeneratedAt) return '#f59e0b';
    return '#e5e7eb';
  };

  // ── Grouped employees by department ──
  const byDept = employees.reduce((acc, emp) => {
    const dept = emp.department || 'No Department';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(emp);
    return acc;
  }, {});

  return (
    <div className="admin-container">
      <h2 style={{ marginBottom: 20 }}>Master Directory</h2>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '2px solid #e5e7eb', paddingBottom: 0 }}>
        {[
          { key: 'employees', label: '📋 Employee Documents' },
          { key: 'hired', label: `🎉 Hired Candidates${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { key: 'onboarded', label: `✅ Onboarded Staff${onboarded.length > 0 && tab !== 'onboarded' ? ` (${onboarded.length})` : ''}` },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: tab === t.key ? 700 : 400,
              borderBottom: tab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
              color: tab === t.key ? '#3b82f6' : '#374151',
              marginBottom: -2, fontSize: 14,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Employee Documents Tab ── */}
      {tab === 'employees' && (
        <div>
          {loading && <p>Loading…</p>}
          {!loading && Object.keys(byDept).length === 0 && <p style={{ color: '#6b7280' }}>No employees found.</p>}
          {Object.entries(byDept).map(([dept, emps]) => (
            <div key={dept} style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 12,
                borderLeft: '4px solid #3b82f6', paddingLeft: 10 }}>
                {dept} <span style={{ fontWeight: 400, color: '#6b7280' }}>({emps.length})</span>
              </h3>
              <div style={{ display: 'grid', gap: 8 }}>
                {emps.map(emp => (
                  <div key={emp.id} style={{ background: '#f9fafb', border: '1px solid #e5e7eb',
                    borderRadius: 8, padding: '12px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 600 }}>{emp.fullName}</span>
                        <span style={{ color: '#6b7280', fontSize: 13, marginLeft: 8 }}>{emp.employeeId}</span>
                      </div>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{emp.email}</span>
                    </div>
                    {emp.equipmentAssignments && emp.equipmentAssignments.length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {emp.equipmentAssignments.map((a, i) => (
                          <span key={i} style={{ fontSize: 12, background: '#dbeafe', color: '#1d4ed8',
                            padding: '2px 8px', borderRadius: 10 }}>
                            {a.deviceType || 'Equipment'} {a.serialNumber ? `· ${a.serialNumber}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Hired Candidates Tab ── */}
      {tab === 'hired' && (
        <div>
          {hiredLoading && <p>Loading…</p>}
          {!hiredLoading && hired.length === 0 && (
            <p style={{ color: '#6b7280' }}>No hired candidates found.</p>
          )}
          <div style={{ display: 'grid', gap: 16 }}>
            {hired.map(c => (
              <div key={c.id} style={{
                border: `2px solid ${cardBorder(c)}`,
                borderRadius: 10, padding: '16px 20px',
                background: '#fff', transition: 'border-color 0.3s',
              }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 16 }}>{c.fullName}</span>
                    <span style={{ marginLeft: 10, color: '#6b7280', fontSize: 13 }}>{c.email}</span>
                    {c.campaign && (
                      <span style={{ marginLeft: 8, fontSize: 12, background: '#ede9fe', color: '#5b21b6',
                        padding: '2px 8px', borderRadius: 10 }}>{c.campaign}</span>
                    )}
                  </div>
                  {/* Progress steps */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {step(!!c.documentsGeneratedAt, 'Docs Generated')}
                    {step(!!c.documentsEmailSentAt, 'Email Sent')}
                    {step(c.allDocumentsSigned, 'Signed')}
                  </div>
                </div>

                {/* Action buttons */}
                {!c.allDocumentsSigned && (
                  <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      onClick={() => doAction(c.id, 'generate-docs', 'generate')}
                      disabled={!!actionLoading[c.id]}
                      style={{
                        padding: '7px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: '#f59e0b', color: '#fff', fontWeight: 600, fontSize: 13,
                        opacity: actionLoading[c.id] === 'generate' ? 0.6 : 1,
                      }}
                    >
                      {actionLoading[c.id] === 'generate' ? 'Generating…' : '📄 Generate Docs'}
                    </button>
                    <button
                      onClick={() => doAction(c.id, 'send-docs', 'send')}
                      disabled={!!actionLoading[c.id] || !c.documentsGeneratedAt}
                      style={{
                        padding: '7px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: c.documentsGeneratedAt ? '#3b82f6' : '#d1d5db',
                        color: '#fff', fontWeight: 600, fontSize: 13,
                        opacity: (actionLoading[c.id] === 'send' || !c.documentsGeneratedAt) ? 0.6 : 1,
                      }}
                    >
                      {actionLoading[c.id] === 'send' ? 'Sending…' : '📧 Send to Candidate'}
                    </button>
                    {actionMsg[c.id] && (
                      <span style={{ fontSize: 13, color: '#059669' }}>{actionMsg[c.id]}</span>
                    )}
                  </div>
                )}

                {/* Timestamps footer */}
                <div style={{ marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#9ca3af' }}>
                  {c.documentsGeneratedAt && <span>Generated: {fmt(c.documentsGeneratedAt)}</span>}
                  {c.documentsEmailSentAt && <span>Sent: {fmt(c.documentsEmailSentAt)}</span>}
                  {c.documentsSignedAt && <span style={{ color: '#059669', fontWeight: 600 }}>Signed: {fmt(c.documentsSignedAt)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Onboarded Staff Tab ── */}
      {tab === 'onboarded' && (
        <div>
          {onboardedLoading && <p>Loading…</p>}
          {!onboardedLoading && onboarded.length === 0 && (
            <p style={{ color: '#6b7280' }}>No completed onboardings yet.</p>
          )}
          <div style={{ display: 'grid', gap: 12 }}>
            {onboarded.map(ob => {
              const u = ob.user || {};
              const tasks = Array.isArray(ob.taskChecklist) ? ob.taskChecklist : [];
              const done = tasks.filter(t => t.completed).length;
              return (
                <div key={ob.id} style={{
                  border: '2px solid #10b981', borderRadius: 10,
                  padding: '14px 20px', background: '#fff',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>
                        {u.firstName} {u.lastName}
                      </span>
                      {u.employeeId && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>{u.employeeId}</span>
                      )}
                      {u.department?.name && (
                        <span style={{ marginLeft: 8, fontSize: 12, background: '#d1fae5', color: '#065f46',
                          padding: '2px 8px', borderRadius: 10 }}>
                          {u.department.name}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, background: '#d1fae5', color: '#065f46',
                        padding: '3px 10px', borderRadius: 12, fontWeight: 600 }}>✓ Onboarded</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                        Tasks: {done}/{tasks.length}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {u.email && <span>{u.email}</span>}
                    <span>Started: {new Date(ob.startDate).toLocaleDateString()}</span>
                    <span>Updated: {new Date(ob.updatedAt).toLocaleDateString()}</span>
                  </div>
                  {tasks.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {tasks.map((t, i) => (
                        <span key={i} style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 10,
                          background: t.completed ? '#d1fae5' : '#f3f4f6',
                          color: t.completed ? '#065f46' : '#6b7280',
                          fontWeight: t.completed ? 600 : 400,
                        }}>
                          {t.completed ? '✓' : '○'} {t.task}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
